import { useState, useEffect, useCallback } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { esStockCritico } from "./utils/formatters";
import {
  fetchEquipos, fetchTickets, fetchDiagnosticos, fetchPresupuestos,
  fetchRepuestos, fetchUsuarios, fetchBitacora, fetchAuditorias,
  fetchMovimientosStock, fetchHistorialEquipos,
  registrarAuditoria as apiRegAuditoria,
  agregarHistorial as apiAgregarHistorial,
  registrarMovimientoStock as apiRegMovimientoStock,
} from "./services/dataService";

import Btn from "./components/ui/Btn";
import Toast from "./components/ui/Toast";
import ModuloReparacion from "./modules/Reparacion";
import ModuloDiagnostico from "./modules/Diagnostico";

import Login from "./components/Login";
import ModuloDashboard from "./modules/Dashboard";
import ModuloEquipos from "./modules/Equipos";
import ModuloTickets from "./modules/Tickets";
import ModuloPresupuestos from "./modules/Presupuestos";
import ModuloInventario from "./modules/Inventario";
import ModuloUsuarios from "./modules/Usuarios";
import ModuloAuditorias from "./modules/Auditorias";
import ModuloReportes from "./modules/Reportes";
import ModuloKPIs from "./modules/KPIS";
import Card from "./components/ui/Card";

import "./index.css";
import logo2 from "./assets/cardiohard-logo2.png"; 

function AppContent() {
  const { usuario, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [darkMode, setDarkMode] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    if (darkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [darkMode]);
  
  const [equipos, setEquipos] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [repuestos, setRepuestos] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [auditorias, setAuditorias] = useState([]);
  const [movimientosStock, setMovimientosStock] = useState([]);
  const [toast, setToast] = useState(null);
  const [historialEquipos, setHistorialEquipos] = useState([]);
  const [bitacoraReparacion, setBitacoraReparacion] = useState([]);
  const [notificaciones, setNotificaciones] = useState([]);
  const [diagnosticos, setDiagnosticos] = useState([]);

  useEffect(() => {
    if (!usuario) return;
    setDataLoading(true);
    Promise.all([
      fetchEquipos().then(setEquipos).catch(() => {}),
      fetchTickets().then(setTickets).catch(() => {}),
      fetchDiagnosticos().then(setDiagnosticos).catch(() => {}),
      fetchPresupuestos().then(setPresupuestos).catch(() => {}),
      fetchRepuestos().then(setRepuestos).catch(() => {}),
      fetchUsuarios().then(setUsuarios).catch(() => {}),
      fetchBitacora().then(setBitacoraReparacion).catch(() => {}),
      fetchAuditorias().then(setAuditorias).catch(() => {}),
      fetchMovimientosStock().then(setMovimientosStock).catch(() => {}),
      fetchHistorialEquipos().then(setHistorialEquipos).catch(() => {}),
    ]).finally(() => setDataLoading(false));
  }, [usuario]);

  const notify = useCallback((message, type = "info") => setToast({ message, type, id: Date.now() }), []);
  const agregarNotificacion = useCallback((mensaje, tipo = "info") => {
    const id = Date.now() + Math.random();
    setNotificaciones(prev => [...prev, { id, mensaje, tipo }]);
    setTimeout(() => setNotificaciones(prev => prev.filter(n => n.id !== id)), 6000);
  }, []);
  
  const notifCount = [
    repuestos.filter(r => esStockCritico(r.stock, r.stock_critico)).length > 0,
    presupuestos.filter(p => p.estado === "Pendiente").length > 0,
  ].filter(Boolean).length;

  const registrarMovimientoStock = useCallback(async (repuesto_id, repuesto_nombre, cantidad, motivo) => {
    const entry = {
      id: Date.now(), repuesto_id, repuesto_nombre, cantidad,
      tipo: cantidad > 0 ? "Suma" : "Resta",
      usuario: usuario?.nombre || "Sistema",
      fecha: new Date().toLocaleString('es-AR'),
      motivo: motivo || ""
    };
    setMovimientosStock(prev => [entry, ...prev]);
    try { await apiRegMovimientoStock(entry); } catch {}
  }, [usuario]);

  const registrarAuditoria = useCallback(async (accion, modulo, resultado = "Éxito") => {
    const entry = {
      id: Date.now(),
      fecha: new Date().toLocaleString('es-AR'),
      usuario: usuario?.nombre || "Sistema",
      accion, modulo, resultado
    };
    setAuditorias(prev => [entry, ...prev]);
    try { await apiRegAuditoria(entry); } catch {}
  }, [usuario]);

  const agregarAlHistorial = useCallback(async (equipoId, equipoSerie, tipo, detalle, tecnico, repuestos = []) => {
    const entry = {
      id: Date.now(), equipo_id: equipoId, equipo_serie: equipoSerie,
      fecha: new Date().toLocaleString('es-AR'), tipo, detalle,
      tecnico: tecnico || usuario?.nombre || "Sistema", repuestos
    };
    setHistorialEquipos(prev => [entry, ...prev]);
    try { await apiAgregarHistorial(entry); } catch {}
  }, [usuario]);

  if (!usuario) return <Login onLogin={() => {}} />;
  if (dataLoading) return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: darkMode ? '#1a202c' : '#f7fafc', color: darkMode ? '#e2e8f0' : '#2d3748'
    }}>
      <div className="loading-spinner" />
      <p style={{ fontSize: '1.125rem', fontWeight: 500, marginTop: '1rem' }}>Cargando datos...</p>
      <p style={{ fontSize: '0.875rem', opacity: 0.7, marginTop: '0.5rem' }}>CardioHard · MedTrack</p>
    </div>
  );
  
  // 🆕 TABS DINÁMICAS SEGÚN ROL
  const tabsDisponibles = [
    { id: "dashboard", label: "Dashboard", icon: "📊" },
    { id: "equipos", label: "Equipos", icon: "🔬" },
    { id: "tickets", label: "Tickets", icon: "🔧" },
    { id: "diagnostico", label: "Diagnóstico", icon: "🔍" },
    // ✅ Solo Admin ve Presupuestos
    ...(usuario?.rol === "Administrador" ? [{ id: "presupuestos", label: "Presupuestos", icon: "💰" }] : []),
    { id: "reparacion", label: "Reparación", icon: "🛠️" },
    { id: "inventario", label: "Inventario", icon: "📦" },
    ...(usuario?.rol !== "Técnico" ? [{ id: "usuarios", label: "Usuarios", icon: "👥" }] : []),
    { id: "auditorias", label: "Auditorías", icon: "📋" },
    ...(usuario?.rol !== "Técnico" ? [{ id: "reportes", label: "Reportes", icon: "📄" }] : []),
    ...(usuario?.rol !== "Técnico" ? [{ id: "kpis", label: "KPIs", icon: "📈" }] : []),
  ];

  return (
    <div className="app">
      <div className="fixed top-4 right-4 z-[9999] space-y-3 pointer-events-none">
        {notificaciones.map(notif => (
          <div key={notif.id} className={`pointer-events-auto p-4 rounded-lg shadow-2xl text-white max-w-sm min-w-[280px] border-l-4 animate-slide-in ${
            notif.tipo === "success" ? "bg-green-700 border-green-400" : 
            notif.tipo === "warning" ? "bg-yellow-700 border-yellow-400" : 
            notif.tipo === "error" ? "bg-red-700 border-red-400" : "bg-blue-700 border-blue-400"
          }`}>
            <div className="flex items-start gap-3">
              <span className="text-xl">{notif.tipo === "success" ? "✅" : notif.tipo === "warning" ? "⚠️" : notif.tipo === "error" ? "❌" : "🔔"}</span>
              <div className="flex-1">
                <p className="font-medium text-sm">{notif.mensaje}</p>
                <p className="text-xs opacity-80 mt-1">CardioHard</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <header className="header">
        <div className="header__content">
          <div className="header__brand">
            <div className="login-logo2"><img src={logo2} alt="CardioHard" /></div>
            <div><p className="header__subtitle">{usuario.rol} · {usuario.nombre}</p></div>
          </div>
          <div className="header__actions">
            {notifCount > 0 && (
              <div className="badge badge--danger badge--clickable" onClick={() => { setTab("dashboard"); notify("Revisá las alertas en el Dashboard", "warning"); }}>
                {notifCount} alerta{notifCount > 1 ? "s" : ""}
              </div>
            )}
            <button onClick={() => setDarkMode(!darkMode)} style={{ background: darkMode ? '#4a5568' : '#edf2f7', border: 'none', borderRadius: '50%', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '20px', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }} title={darkMode ? "Modo Claro" : "Modo Oscuro"}>
              {darkMode ? "☀️" : "🌙"}
            </button>
            <div className="avatar" title={usuario.nombre}>{usuario.nombre.split(" ").map(n => n[0]).join("").slice(0,2).toUpperCase()}</div>
            <Btn small variant="default" onClick={logout} title="Cerrar sesión">🚪 Salir</Btn>
          </div>
        </div>
        <nav className="nav">
          {tabsDisponibles.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`nav__item ${tab === t.id ? "nav__item--active" : ""}`}>
              <span>{t.icon}</span> {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="main">
        {tab === "dashboard" && <ModuloDashboard equipos={equipos} tickets={tickets} repuestos={repuestos} presupuestos={presupuestos} auditorias={auditorias} movimientosStock={movimientosStock} onNotify={notify} />}
        {tab === "equipos" && <ModuloEquipos equipos={equipos} setEquipos={setEquipos} tickets={tickets} setTickets={setTickets} usuarios={usuarios} historialEquipos={historialEquipos} agregarAlHistorial={agregarAlHistorial} registrarAuditoria={registrarAuditoria} onNotify={notify} />}
        {tab === "tickets" && <ModuloTickets tickets={tickets} setTickets={setTickets} setEquipos={setEquipos} equipos={equipos} usuarios={usuarios} presupuestos={presupuestos} setPresupuestos={setPresupuestos} setTab={setTab} agregarAlHistorial={agregarAlHistorial} agregarNotificacion={agregarNotificacion} registrarAuditoria={registrarAuditoria} onNotify={notify} />}
        {tab === "diagnostico" && <ModuloDiagnostico equipos={equipos} tickets={tickets} diagnosticos={diagnosticos} setDiagnosticos={setDiagnosticos} agregarAlHistorial={agregarAlHistorial} registrarAuditoria={registrarAuditoria} onNotify={notify} setTab={setTab} />}
        
        {tab === "presupuestos" && usuario?.rol !== "Administrador" ? (
          <Card className="text-center py-8">
            <p className="text-muted mb-4">🔐 Acceso restringido. Solo administradores pueden gestionar presupuestos.</p>
            <Btn variant="primary" onClick={() => setTab("dashboard")}>Volver al Dashboard</Btn>
          </Card>
        ) : (
          tab === "presupuestos" && <ModuloPresupuestos presupuestos={presupuestos} setPresupuestos={setPresupuestos} tickets={tickets} setTickets={setTickets} equipos={equipos} setEquipos={setEquipos} repuestos={repuestos} registrarAuditoria={registrarAuditoria} onNotify={notify} activeTab={tab} />
        )}
        
        {tab === "reparacion" && <ModuloReparacion equipos={equipos} setEquipos={setEquipos} tickets={tickets} setTickets={setTickets} bitacoraReparacion={bitacoraReparacion}  setBitacoraReparacion={setBitacoraReparacion} repuestos={repuestos} setRepuestos={setRepuestos} registrarMovimientoStock={registrarMovimientoStock} registrarAuditoria={registrarAuditoria} onNotify={notify} />}
        {tab === "inventario" && <ModuloInventario repuestos={repuestos} setRepuestos={setRepuestos} registrarMovimientoStock={registrarMovimientoStock} registrarAuditoria={registrarAuditoria} onNotify={notify} />}
        {tab === "usuarios" && <ModuloUsuarios usuarios={usuarios} setUsuarios={setUsuarios} registrarAuditoria={registrarAuditoria} onNotify={notify} />}
        {tab === "auditorias" && <ModuloAuditorias auditorias={auditorias} onNotify={notify} />}
        {tab === "reportes" && <ModuloReportes equipos={equipos} tickets={tickets} presupuestos={presupuestos} auditorias={auditorias} onNotify={notify} />}
        {tab === "kpis" && <ModuloKPIs />}
      </main>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <footer className="footer">
        <span>CardioHard · 🔐</span>
        <span>© 2026 · Sesión: {usuario?.nombre} 👋</span>
      </footer>
    </div>
  );
}

export default function App() {
  return <AuthProvider><AppContent /></AuthProvider>;
}